import type { ILimitedListItemModel } from "../types/limitedListItemModel";
import { LimitedListItemType } from "../types/limitedListItemModel";

const createItemFactory =
    (creditsPerRecord: number) =>
    (value: number, type: LimitedListItemType, isAvailable: boolean = true): ILimitedListItemModel => ({
        value,
        cost: value * creditsPerRecord,
        type,
        isAvailable,
    });

function getLimitedListOfItems(
    listOfItems: readonly number[],
    remainingCredits: number,
    creditsPerRecord: number,
    numberOfResults: number,
    minimalValue: number,
    isAllowValueOverMinimalValue: boolean = true,
): ILimitedListItemModel[] {
    if (!listOfItems.length || !numberOfResults) {
        return [];
    }

    const createItem = createItemFactory(creditsPerRecord);

    const lastItemValue: number = listOfItems[listOfItems.length - 1];
    const remainingRecordsQuota: number = Math.floor(remainingCredits / creditsPerRecord);

    // remove items over numberOfResults AND remove items bigger than minimalValue AND over remainingRecordsQuota
    const newListOfItems: number[] = [...listOfItems].filter(
        (item) => item <= numberOfResults && (item <= minimalValue || item <= remainingRecordsQuota),
    );

    // add minimalValue if needed
    if (newListOfItems[newListOfItems.length - 1] < minimalValue && minimalValue <= numberOfResults) {
        newListOfItems.push(minimalValue);
    }

    // transform items to IResultItem[]
    const resultItemsList: ILimitedListItemModel[] = newListOfItems.map((value) =>
        createItem(value, LimitedListItemType.Usual, value < remainingRecordsQuota),
    );

    if (
        numberOfResults < lastItemValue &&
        numberOfResults < remainingRecordsQuota &&
        (numberOfResults < minimalValue || isAllowValueOverMinimalValue)
    ) {
        resultItemsList.push(createItem(numberOfResults, LimitedListItemType.All));
    } else if (
        remainingRecordsQuota &&
        (remainingRecordsQuota < minimalValue || isAllowValueOverMinimalValue) &&
        remainingRecordsQuota < lastItemValue
    ) {
        const indexOfFirstUnavailable = resultItemsList.findIndex(({ isAvailable }) => !isAvailable);

        if (indexOfFirstUnavailable === -1) {
            resultItemsList.push(createItem(remainingRecordsQuota, LimitedListItemType.Remaining));
        } else {
            resultItemsList.splice(
                indexOfFirstUnavailable,
                0,
                createItem(remainingRecordsQuota, LimitedListItemType.Remaining),
            );
        }
    }

    return resultItemsList;
}

export default getLimitedListOfItems;
